import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Link from 'next/link';
import { useRouter } from 'next/router';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import { CATEGORIES } from '@r1-frontend/shared/constants/analytics';

import ChannelListPopup from '~/src/components/Bundles/ChannelListPopup';
import { TariffCardPlug } from '~/src/components/TariffCardPlug';
import { TariffsFilters } from '~/src/components/TariffsFilters';
import { ETariffTypes } from '~/src/components/TariffsFilters/filterConstants';
import { ITariff } from '~/src/entities/formattedTariff';
import IndentContainerWithBackground from '~/src/features/internetV2/components/IndentContainerWithBackground';
import { PromoTextWithStyleHeading2 } from '~/src/features/internetV2/components/typography';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';
import { TState } from '~/src/store';
import { selectAllTariffs } from '~/src/store/selectors/filteredBundles';

import * as ST from './styled';

const sendAnalytics = getAnalyticsSender(CATEGORIES.tariffCards);

export const TariffsContainer = (): JSX.Element => {
    const tariffs = useSelector(selectAllTariffs);
    const { isAuth } = useSelector((state: TState) => state.auth);

    const router = useRouter();

    const [isOpenChannelsPopup, setIsOpenChannelsPopup] = useState(false);
    const [selectedTariff, setSelectedTariff] = useState<ITariff | null>(null);

    const handleOpenPopup = (tariff: ITariff) => {
        setIsOpenChannelsPopup(true);
        setSelectedTariff(tariff);
    };

    const handleClosePopup = () => {
        setIsOpenChannelsPopup(false);
    };

    /**
     * TODO вынести в селектор через reselect
     */
     const initTariffs = useMemo(
        () =>
            tariffs.filter(
                (tariff: ITariff) =>
                    !!tariff.availableOnInternetPage && !tariff.isCottage,
            ),
        [tariffs],
    );

    return (
        <IndentContainerWithBackground
            $desktop={[80, 0]}
            $mobile={[36, 0]}
            id={'Tariffs'}
        >
            <WideContainer>
                <IndentContainer $united={[0, 0, 24]}>
                    <PromoTextWithStyleHeading2 as={'h2'}>Наши самые выгодные тарифы</PromoTextWithStyleHeading2>
                </IndentContainer>
                <TariffsFilters
                    isVillage={false}
                    initTariffs={initTariffs}
                    defaultValue={ETariffTypes.FILTER_INTERNET}
                    menuItems={[
                        {
                            text: 'Для квартиры',
                            action: () => {
                                router.push('/internet');
                                sendAnalytics('click apartment');
                            },
                        },
                        {
                            text: 'Для частного дома',
                            action: () => {
                                router.push('/internet/mono-village');
                                sendAnalytics('click village');
                            },
                        },
                    ]}
                    hasCityTariffs={true}
                    hasVillageTariffs={true}
                >
                    {(filteredTariffs) =>
                        filteredTariffs.length > 0
                            ? <ST.Tariffs
                                sortField={'sortOnInternet'}
                                baseTariffs={filteredTariffs}
                                openPopup={handleOpenPopup}
                                isAuth={isAuth}
                            />
                            : <TariffCardPlug/>
                    }
                </TariffsFilters>
                <ButtonGroup>
                    <Link href={'/bundles'} passHref>
                        <Button
                            size={'large'}
                            bType={'secondary'}
                        >
                            Все тарифы
                        </Button>
                    </Link>
                </ButtonGroup>
            </WideContainer>

            <ChannelListPopup
                isOpen={isOpenChannelsPopup}
                onClose={handleClosePopup}
                freePackets={selectedTariff?.freePackets}
            />
        </IndentContainerWithBackground>
    );
};
